import * as React from "react";
import {useMemo, useState, useCallback, useRef} from "react";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FileImportUploader from "../FileImportUploader";
import Link from "@mui/material/Link";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";


// -----------------------------
// Types
// -----------------------------
import type {ContactListsTableProps, EmailList} from "../../../../types/emailList"
import {useDeleteTargetEmailListMutation} from "../../../../services/emailListApi";
import AddNewEmailList from "../AddNewEmailList";
import MergeContactsDialog from "./MergeContactsDialog";
import ReplaceContactsDialog from "./ReplaceContactsDialog";

type Order = "asc" | "desc";

// -----------------------------
// Utilities
// -----------------------------
const fmtInt = (n: number) => n.toLocaleString();

function getComparator<Key extends keyof EmailList>(
  order: Order,
  orderBy: Key
): (a: EmailList, b: EmailList) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const av = (a as any)[orderBy];
  const bv = (b as any)[orderBy];
  if (orderBy === "createdAt") {
    const ad = new Date(av).getTime();
    const bd = new Date(bv).getTime();
    if (bd < ad) return -1;
    if (bd > ad) return 1;
    return 0;
  }
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

// -----------------------------
// Header Bar (folder dropdown + search)
// -----------------------------
interface HeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
}

function HeaderBar({search, onSearchChange}: HeaderProps) {
  return (
    <Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems={{xs: "stretch", md: "center"}}
           justifyContent="space-between" sx={{mb: 2}}>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{minWidth: 260}}
      >
        <FileImportUploader/>
        <AddNewEmailList/>

      </Stack>

      <TextField
        placeholder="Search a list name or ID"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon/>
            </InputAdornment>
          )
        }}
        sx={{minWidth: 320}}
      />
    </Stack>
  );
}

// -----------------------------
// Row actions menu
// -----------------------------
interface RowMenuState {
  anchorEl: HTMLElement | null;
  row: EmailList | null;
}

function ActionsMenu({state, onClose}: { state: RowMenuState; onClose: (action?: string) => void }) {

  const open = Boolean(state.anchorEl);
  return (
    <Menu
      anchorEl={state.anchorEl}
      open={open}
      onClose={() => onClose()}
      anchorOrigin={{vertical: "bottom", horizontal: "right"}}
      transformOrigin={{vertical: "top", horizontal: "right"}}
    >
      <MenuItem onClick={() => onClose("replace")}> <ContentCopyIcon fontSize="small" style={{marginRight: 8}}/> Replace
        contacts</MenuItem>
      <MenuItem onClick={() => onClose("merge")}> <FileUploadIcon fontSize="small" style={{marginRight: 8}}/> Merge
        contacts</MenuItem>
      <MenuItem onClick={() => onClose("Edit")}> <EditIcon fontSize="small"
                                                           style={{marginRight: 8}}/> Edit</MenuItem>

      <Divider component="li" sx={{my: 0.5}}/>

      <MenuItem onClick={() => onClose("delete")} sx={{color: "error.main"}}> <DeleteForeverIcon fontSize="small"
                                                                                                 style={{marginRight: 8}}/> Delete
        list</MenuItem>
    </Menu>
  );
}


// -----------------------------
// Main Component
// -----------------------------
export default function ContactListsTable({emailLists}: ContactListsTableProps) {
  // Local state
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof EmailList>("createdAt");
  const [menu, setMenu] = useState<RowMenuState>({anchorEl: null, row: null});
  const [deleteTargetEmailListApi] = useDeleteTargetEmailListMutation();
  const navigate = useNavigate();
  
  // Dialog states
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [dialogEmailList, setDialogEmailList] = useState<EmailList | null>(null);

  const visible = useMemo(() => {
    const filtered = emailLists.filter((r) =>
      (search.trim() === "" || r.title.toLowerCase().includes(search.toLowerCase()) || `#${r.id}`.includes(search.replace("#", "")))
    );
    return filtered.sort(getComparator(order, orderBy));
  }, [emailLists, search, order, orderBy]);

  const handleRequestSort = (property: keyof EmailList) => (
    event: React.MouseEvent<unknown>
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, row: EmailList) => {
    setMenu({anchorEl: e.currentTarget, row});
  };
  const closeMenu = async (action?: string) => {
    if (action && menu.row) {
      console.log("Action:", action, "row:", menu.row);
      try {
        switch (action.toLowerCase()) {
          case "replace":
            setDialogEmailList(menu.row);
            setReplaceDialogOpen(true);
            break;
          case "merge":
            setDialogEmailList(menu.row);
            setMergeDialogOpen(true);
            break;
          case "delete":
            await deleteTargetEmailListApi(menu.row.id).unwrap();
            break
          case "edit":
            console.log("edit", menu.row.id);
            navigate(`/emailList/${menu.row.id}/target`);
            break
        }
      } catch (e) {
        console.error("Error:", e);
        //toast.error("Failed to perform action", e);
      }
    }
    setMenu({anchorEl: null, row: null});
  };

  return (
    <Box sx={{p: {xs: 2, md: 3}}}>
      <HeaderBar
        search={search}
        onSearchChange={setSearch}
      />

      <TableContainer component={Paper} elevation={0}
                      sx={{borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}`}}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>Lists</TableCell>
              <TableCell align="right">Contacts</TableCell>
              <TableCell sortDirection={orderBy === "createdAt" ? order : false}>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={handleRequestSort("createdAt")}
                >Creation date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visible.map((row) => {
              const labelId = `lists-checkbox-${row.id}`;
              return (
                <TableRow key={row.id} hover sx={{cursor: "pointer"}}>
                  <TableCell component="th" id={labelId} scope="row"
                             sx={{color: "primary.main", textDecoration: "underline"}}>

                    <Link component={RouterLink} to={`/emailList/${row.id}/target`}>
                      {row.title}
                    </Link>

                  </TableCell>

                  <TableCell align="right">{fmtInt(row.contacts)}</TableCell>
                  <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>

                  <TableCell align="right">{row.status}</TableCell>
                  <><TableCell align="right" sx={{width: 72}}>
                    <IconButton onClick={(e) => openMenu(e as any, row)} aria-label="row actions">
                      <MoreVertIcon/>
                    </IconButton>
                  </TableCell></>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ActionsMenu state={menu} onClose={closeMenu}/>
      
      <ReplaceContactsDialog 
        open={replaceDialogOpen} 
        onClose={() => setReplaceDialogOpen(false)} 
        emailList={dialogEmailList}
      />
      
      <MergeContactsDialog 
        open={mergeDialogOpen} 
        onClose={() => setMergeDialogOpen(false)} 
        emailList={dialogEmailList}
      />
    </Box>
  );
}

// Expose header if you want to reuse it elsewhere
export {HeaderBar};
